// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React from 'react';
import {
    useDispatch,
    useSelector,
} from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
    DefaultButton,
    Spinner,
    SpinnerSize
} from 'office-ui-fabric-react';

import ErrorMessage from './../../components/ErrorMessage';
import {
    putPartInfo,
    resetParts
} from './../../store/orderCreation';

import Styles from './styles.module.scss';
import { LoadingState } from './../../store/actionStatus';
import { logPageAction } from 'src/lib/ApplicationInsights';

interface Props {
    deviceName: string;
    serialNumber: string;
    image: string;
    regex?: string;
}

interface RenderProps {
    render: boolean;
    response: any;
}

export default function ConfirmSerialReplacement(props: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const partInfo = useSelector((state: any) => state.order.parts[props.serialNumber]);
    const loadingState = useSelector((state: any) => (state.actionStatus[`PUT_PART_INFO-${props.serialNumber}`] || {}));
    const [value, setValue] = React.useState('');
    const [isNotValidSerialNumber, setNotValidSerialNumber] = React.useState(false);
    const [validationMessage, setvalidationMessage] = React.useState('');
    const regex = new RegExp(props.regex || '');

    const response = partInfo ? {
        image: partInfo.part.repairProduct.imageUri,
        name: partInfo.part.repairProduct.name,
        serial: partInfo.serialNumber,
    } : {};

    const inputClass = clsx(
        Styles.input,
        { [Styles.errorInput]: identifyErrorMessage() }
    );

    function identifyErrorMessage() {
        let errorMessage = '';
        if (isNotValidSerialNumber) {
            errorMessage = validationMessage;
        } else if (loadingState.status === LoadingState.rejected) {
            errorMessage = t('error_occurred');
        }
        return errorMessage;
    }

    function HandleChange(event: any) {
        setValue(event.target.value);
    }

    function OnConfirm() {
        logPageAction('Select confirm button');

        // When no regex is provided from the service call we have no fallback
        // regex to check whether a serial number is valid. In this case, client
        // will assume all inputs provided are valid (regex is found in the
        // order.selectedParts in the store).
        if (!regex.test(value)) {
            dispatch(resetParts({
                deviceSerial: props.serialNumber,
                partSerial: value,
            }));
            setvalidationMessage(t('please_provide_valid_serial_number'));
            return setNotValidSerialNumber(true);
        }
        if (props.serialNumber === value) {
            dispatch(resetParts({
                deviceSerial: props.serialNumber,
                partSerial: value,
            }));
            setvalidationMessage(t('register_device_message_alert'));
            return setNotValidSerialNumber(true);
        }
        setNotValidSerialNumber(false);
        dispatch(putPartInfo({
            deviceSerial: props.serialNumber,
            partSerial: value,
        }));
    }

    const isLoading = loadingState.status === LoadingState.started;
    const traceData = { loadingState, partInfo, replacementSerialNumber: value };

    const confirmButtonClass = clsx(
        Styles.confirm,
        {
            [Styles.disabled]: !value,
            [Styles.disabledButton]: isLoading,
        }
    );

    return (
        <section className={Styles.main}>
            <img
                alt="device-image"
                className={Styles.image}
                src={props.image}
            />
            <div
                aria-colcount={3}
                aria-label={t('part_replacement')}
                aria-rowcount={1}
                className={Styles.table}
                role="table"
            >
                <div className={Styles.tableHeaders} role="row">
                    <div
                        aria-colindex={0}
                        className={Styles.tableHeader}
                        role="columnheader"
                    >
                        {t('part_replacement')}
                    </div>
                    <div
                        aria-colindex={1}
                        className={Styles.tableHeader}
                        role="columnheader"
                    >
                        {t('serial_number')}
                    </div>
                    <div
                        aria-colindex={2}
                        className={Styles.tableHeader}
                        role="columnheader"
                    >
                        {t('new_serial_number')}*
                    </div>
                </div>
                <div className={Styles.tableRow} role="row">
                    <div
                        aria-colindex={0}
                        className={Styles.tableColumn}
                        role="gridcell"
                    >
                        <div>{props.deviceName}</div>
                        <div className={Styles.errorMsg}>
                            {RenderError(identifyErrorMessage(), traceData)}
                        </div>
                    </div>
                    <div
                        aria-colindex={1}
                        className={Styles.tableColumn}
                        role="gridcell"
                    >
                        {props.serialNumber}
                    </div>
                    <div
                        aria-colindex={2}
                        className={Styles.tableColumn}
                        role="gridcell"
                    >
                        <input
                            aria-label={t('new_serial_number')}
                            autoFocus={true}
                            required={true}
                            className={inputClass}
                            onChange={HandleChange}
                            value={value}
                        />
                        <div className={Styles.submitAndSpinnerContainer}>
                            <DefaultButton
                                allowDisabledFocus={true}
                                className={confirmButtonClass}
                                disabled={isLoading}
                                text={t('confirm')}
                                onClick={OnConfirm}
                            />
                            {isLoading && (
                                <Spinner
                                    className={Styles.spinnerStyle}
                                    size={SpinnerSize.medium}
                                />
                            )}
                        </div>
                    </div>
                    {!isNotValidSerialNumber && (
                        <RenderResponse
                            render={loadingState.status === LoadingState.success}
                            response={response}
                        />
                    )}
                </div>
            </div>
        </section>
    );
}

function RenderError(message: string, traceData?: any) {
    return (
        <ErrorMessage message={message} traceData={traceData} />
    );
}

function RenderResponse(renderProps: RenderProps) {
    const { t } = useTranslation();
    const { render, response } = { ...renderProps };

    if (!render) return null;
    return (
        <div className={Styles.tableColumn}>
            <div className={Styles.serviceResponse}>
                <img
                    alt="replacement-device-image"
                    className={Styles.icon}
                    src={response.image}
                />
                <div className={Styles.details}>
                    <div className={Styles.detailName}>{response.name}</div>
                    <div className={Styles.detailLabel}>{t('part_serial_number')}</div>
                    <div className={Styles.detailValue}>{response.serial}</div>
                </div>
            </div>
        </div>
    );
}
